import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Avatar,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  // ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  listItemButtonClasses,
} from '@mui/material';
import { Stack } from '@mui/system';
import {
  Barcode,
  ChartSquare,
  Concierge,
  DangerOutline,
  Flag,
  ListIcon,
  Logout,
  ProfileCircle,
  ReceiptItem,
  SettingOutline,
  StarOutline,
  UserTag,
} from 'asset/icons';
import MenuLineHorizontal from 'asset/icons/MenuLineHorizontal';
import { drawerWidth } from 'config';
// import Cookies from 'js-cookie';
import { parseJwt } from 'helpers';
import React, { Fragment, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { LinkProps, Link as RouterLink } from 'react-router-dom';
import * as CONSTANTS from '../../../config/constants';

interface ListItemLinkProps {
  icon?: (active: boolean) => React.ReactElement;
  primary: string;
  to: string;
}

const data_list_menu = [
  {
    label: 'Dashboard',
    href: '/',
    icon: (active: boolean) => <ChartSquare active={active} />,
  },
  {
    label: 'Accounts',
    href: '',
    icon: (active: boolean) => <ProfileCircle active={active} />,
    child: [
      { label: 'Parents', href: '/accounts/parents' },
      { label: 'Sitters', href: '/accounts/sitters' },
      { label: 'Nanny', href: '/accounts/nanny' },
      { label: 'Admins', href: '/accounts/admins', roles: ['superadmin'] },
      { label: 'All User', href: '/accounts/users', roles: ['superadmin'] },
    ],
  },
  {
    label: 'Requests',
    href: '',
    icon: (active: boolean) => <UserTag active={active} />,
    child: [
      { label: 'Just Once', href: '/requests/just-once' },
      { label: 'Recurring', href: '/requests/recurring' },
    ],
  },
  {
    label: 'Jobs',
    href: '/jobs',
    icon: (active: boolean) => <ListIcon active={active} />,
  },
  {
    label: 'Nanny Booking',
    href: '/nanny-bookings',
    icon: (active: boolean) => <ListIcon active={active} />,
  },
  {
    label: 'Concierge',
    href: '',
    icon: (active: boolean) => <Concierge active={active} />,
    child: [
      { label: 'Overview', href: '/concierge/parent-overviews' },
      { label: 'Subscription', href: '/concierge/subscription' },
      { label: 'Sitters', href: '/concierge/sitters-ac' },
      { label: 'Packages', href: '/concierge/package' },
      {
        label: 'Payouts',
        href: '/concierge/ac-payouts',
        roles: ['superadmin'],
      },
      {
        label: 'Top-ups',
        href: '/concierge/top-ups',
        roles: ['superadmin'],
      },
      { label: 'Transactions', href: '/concierge/transactions' },
      { label: 'Activity Log', href: '/concierge/activity-logs' },
      {
        label: 'Public Holiday',
        href: '/concierge/public-holiday',
        roles: ['superadmin', 'staff'],
      },
    ],
  },
  {
    label: 'Finance',
    href: '',
    icon: (active: boolean) => <ReceiptItem active={active} />,
    child: [
      { label: 'Transactions', href: '/finance/transactions' },
      { label: 'Top-ups', href: '/finance/top-ups', roles: ['superadmin'] },
      { label: 'Payouts', href: '/finance/payouts', roles: ['superadmin'] },
      { label: 'Cash Fee', href: '/finance/cash-fee', roles: ['superadmin'] },
    ],
  },
  {
    label: 'Reports',
    href: '/reports',
    icon: (active: boolean) => <DangerOutline active={active} />,
  },
  {
    label: 'Promotions',
    href: '/promotions',
    icon: (active: boolean) => <Barcode active={active} />,
  },
  {
    label: 'Reviews',
    href: '/reviews',
    icon: (active: boolean) => <StarOutline active={active} />,
  },
  {
    label: 'User Flags',
    href: '/user-flags',
    icon: (active: boolean) => <Flag active={active} />,
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: (active: boolean) => <SettingOutline active={active} />,
    roles: ['superadmin'],
  },
  {
    label: 'Finance Settings',
    href: '/finance-settings',
    icon: (active: boolean) => <SettingOutline active={active} />,
    roles: ['superadmin'],
  },
];

const ListItemLink = (props: ListItemLinkProps) => {
  const { icon, primary, to } = props;
  const { pathname } = useLocation();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef<HTMLAnchorElement, Omit<LinkProps, 'to'>>(function Link(
        itemProps,
        ref
      ) {
        return <RouterLink to={to} ref={ref} {...itemProps} role={undefined} />;
      }),
    [to]
  );

  // Check if current path matches or starts with the menu item path
  // Special case: root path '/' should only match exactly
  const isActive =
    to === '/'
      ? pathname === '/'
      : Boolean(matchPath(to, pathname)?.pathname!) || pathname.startsWith(to);

  return (
    <ListItem
      disablePadding
      sx={{
        padding: '2px 10px 2px 9px',
      }}
    >
      <ListItemButton
        sx={{
          padding: '7px 12px',
          borderRadius: '5px',
          color: '#666666',
          ':hover': {
            background: '#F1F1F1',
          },
          [`&.${listItemButtonClasses.selected}`]: {
            color: 'white.light',
            backgroundColor: 'primary.main',
            ':hover': {
              backgroundColor: 'primary.main',
            },
          },
        }}
        selected={isActive}
        component={renderLink}
      >
        <ListItemIcon
          sx={{
            minWidth: icon ? 'fit-content' : '24px',
          }}
        >
          {icon ? icon(isActive) : null}
        </ListItemIcon>
        <ListItemText
          primary={primary}
          sx={{
            marginLeft: '16px',
            my: '0px',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const MenuLeft = () => {
  const [openAccounts, setOpenAccounts] = useState(false);
  const [openRequests, setOpenRequests] = useState(false);
  const [openAC, setOpenAC] = useState(false);
  const [openTransactions, setOpenTransactions] = useState(false);
  const { pathname } = useLocation();
  const token = localStorage.getItem(CONSTANTS.ACCESS_TOKEN);
  const tokenPayload = token ? parseJwt(token) : null;
  // filter data_list_menu by roles (tokenPayload.data.admin_role in roles)
  // and need check child item roles
  const filteredDataListMenu = data_list_menu
    .filter((item) => {
      if (item.roles) {
        return item.roles.includes(tokenPayload?.data?.admin_role);
      }
      return true;
    })
    .map((item) => {
      if (item.child) {
        item.child = item.child.filter((child) => {
          if (child.roles) {
            return child.roles.includes(tokenPayload?.data?.admin_role);
          }
          return true;
        });
      }
      return item;
    });

  const handleClick = (txt: string) => {
    switch (txt) {
      case 'Accounts':
        setOpenAccounts(!openAccounts);
        break;
      case 'Requests':
        setOpenRequests(!openRequests);
        break;
      case 'Finance':
        setOpenTransactions(!openTransactions);
        break;
      case 'Concierge':
        setOpenAC(!openAC);
        break;
    }
  };

  const onClickLogout = () => {
    localStorage.clear();
    window.location.reload();
  };

  useEffect(() => {
    if (
      Boolean(matchPath('/accounts/parents', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/parents/*', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/sitters', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/sitters/*', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/admins', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/admins/*', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/users', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/users/*', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/nanny', pathname)?.pathname!) ||
      Boolean(matchPath('/accounts/nanny/*', pathname)?.pathname!)
    )
      setOpenAccounts(true);
    if (
      Boolean(matchPath('/finance/transactions', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/transactions/*', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/top-ups', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/top-ups/*', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/payouts', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/payouts/*', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/cash-fee', pathname)?.pathname!) ||
      Boolean(matchPath('/finance/cash-fee/*', pathname)?.pathname!)
    )
      setOpenTransactions(true);
    if (
      Boolean(matchPath('/requests/just-once', pathname)?.pathname!) ||
      Boolean(matchPath('/requests/just-once/*', pathname)?.pathname!) ||
      Boolean(matchPath('/requests/recurring', pathname)?.pathname!) ||
      Boolean(matchPath('/requests/recurring/*', pathname)?.pathname!)
    )
      setOpenRequests(true);
    if (
      Boolean(matchPath('/concierge/parent-overviews', pathname)?.pathname!) ||
      Boolean(
        matchPath('/concierge/parent-overviews/*', pathname)?.pathname!
      ) ||
      Boolean(matchPath('/concierge/package', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/package/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/public-holiday', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/public-holiday/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/subscription', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/subscription/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/ac-payouts', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/ac-payouts/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/activity-logs', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/activity-logs/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/sitters-ac', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/sitters-ac/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/top-ups', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/top-ups/*', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/transactions', pathname)?.pathname!) ||
      Boolean(matchPath('/concierge/transactions/*', pathname)?.pathname!)
    )
      setOpenAC(true);
  }, [pathname]);

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          padding: { xs: '0px 12px 0px 20px' },
        }}
      >
        <Stack direction="row" spacing="15px">
          <Stack>
            <MenuLineHorizontal />
          </Stack>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              color: '#8E8E93',
            }}
          >
            Aunty CMS
          </Typography>
        </Stack>
        <Avatar
          sx={{ background: 'rgba(255, 121, 110, 0.2)', width: 30, height: 30 }}
        >
          N
        </Avatar>
      </Toolbar>
      <Divider />
      <List
        disablePadding
        sx={{
          paddingTop: '23px',
        }}
      >
        {filteredDataListMenu.map((item, index) => {
          if (index === 1 || index === 2 || index === 5 || index === 6) {
            return (
              <Fragment key={item.label}>
                <ListItem
                  key={item.label}
                  disablePadding
                  sx={{
                    padding: '2px 10px 2px 9px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      padding: '7px 12px',
                      borderRadius: '5px',
                      color: '#666666',
                      ':hover': {
                        background: '#F1F1F1',
                      },
                      [`&.${listItemButtonClasses.selected}`]: {
                        color: 'white.light',
                        backgroundColor: 'primary.main',
                        ':hover': {
                          backgroundColor: 'primary.main',
                        },
                      },
                    }}
                    selected={
                      index === 1
                        ? openAccounts
                        : index === 2
                        ? openRequests
                        : index === 5
                        ? openAC
                        : openTransactions
                    }
                    onClick={() => handleClick(item.label)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'fit-content',
                      }}
                    >
                      {item.icon(
                        index === 1
                          ? openAccounts
                          : index === 2
                          ? openRequests
                          : index === 5
                          ? openAC
                          : openTransactions
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        marginLeft: '16px',
                        my: '0px',
                      }}
                    />
                    {index === 1 ? (
                      openAccounts ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    ) : index === 2 ? (
                      openRequests ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    ) : index === 5 ? (
                      openAC ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    ) : openTransactions ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={
                    index === 1
                      ? openAccounts
                      : index === 2
                      ? openRequests
                      : index === 5
                      ? openAC
                      : openTransactions
                  }
                >
                  <List disablePadding>
                    {item.child?.map((item) => (
                      <ListItemLink
                        primary={item.label}
                        to={item.href}
                        key={item.label}
                      />
                    ))}
                  </List>
                </Collapse>
              </Fragment>
            );
          }
          if (item.href) {
            return (
              <ListItemLink
                icon={item.icon}
                primary={item.label}
                to={item.href}
                key={item.label}
              />
            );
          }
          return (
            <ListItem
              disablePadding
              sx={{
                padding: '2px 10px 2px 9px',
              }}
              key={item.label}
            >
              <ListItemButton
                sx={{
                  padding: '7px 12px',
                  borderRadius: '5px',
                  color: '#666666',
                  ':hover': {
                    background: '#F1F1F1',
                  },
                  [`&.${listItemButtonClasses.selected}`]: {
                    color: 'white.light',
                    backgroundColor: 'primary.main',
                    ':hover': {
                      backgroundColor: 'primary.main',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 'fit-content',
                  }}
                >
                  {item.icon(false)}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    marginLeft: '16px',
                    my: '0px',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem
          disablePadding
          sx={{
            padding: '2px 10px 2px 9px',
          }}
        >
          <ListItemButton
            sx={{
              padding: '7px 12px',
              borderRadius: '5px',
              color: '#666666',
              ':hover': {
                background: '#F1F1F1',
              },
              [`&.${listItemButtonClasses.selected}`]: {
                color: 'white.light',
                backgroundColor: 'primary.main',
                ':hover': {
                  backgroundColor: 'primary.main',
                },
              },
            }}
            onClick={() => onClickLogout()}
          >
            <ListItemIcon
              sx={{
                minWidth: 'fit-content',
              }}
            >
              <Logout active={false} />
            </ListItemIcon>
            <ListItemText
              primary={'Logout'}
              sx={{
                marginLeft: '16px',
                my: '0px',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

export default MenuLeft;
